import { catalogReferenceClient } from "../catalogApprovals/catalogReferenceOptions";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useCatalogAccess from "../useCatalogAccess";
import { readStoredUser } from "../../../utils/sessionUser";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import InternalCodeSuggestions from "../InternalCodeSuggestions";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import { useCatalogTaxonomyText, useCatalogTaxonomyDialog } from "../catalogTaxonomyMessages";
import { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";

const buildEmptyForm = () => ({
  code: "",
  name: "",
  description: "",
  subfamily_id: "",
});

export default function AddCategoryPopup({ isOpen, onClose, onAdded, existingRecords = [] }) {
  const t = useCatalogTaxonomyText();
  const { languageId } = usePmsTheme();
  const businessUnitId = useRef(readStoredUser()?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const permission = "purchasing.categories.create";
  const canCreate = access.can(permission);
  const [form, setForm] = useState(buildEmptyForm());
  const [subfamilies, setSubfamilies] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const dialogRef = useCatalogTaxonomyDialog(isOpen && canCreate && !pendingRequest, () => handleClose(), saving);

  useEffect(() => {
    if (!isOpen || !canCreate) return undefined;
    let cancelled = false;
    const stamp = access.capture();

    const loadSubfamilies = async () => {
      try {
        const response = await catalogReferenceClient(axios, languageId).get("/api/purchasing/subfamilies", access.requestConfig(permission));
        if (cancelled || !access.isCurrent(stamp, permission)) return;

        const sorted = [...(response.data || [])].sort((left, right) => {
          const leftFamily = String(left?.family?.name || "");
          const rightFamily = String(right?.family?.name || "");
          if (leftFamily !== rightFamily) {
            return leftFamily.localeCompare(rightFamily, undefined, {
              sensitivity: "base",
            });
          }
          return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          });
        });

        setSubfamilies(sorted);
      } catch (requestError) {
        console.error("[CATEGORIES][ADD][SUBFAMILIES][ERROR]", requestError);
        if (!cancelled && access.isCurrent(stamp, permission)) {
          setSubfamilies([]);
          setError("We could not load the subfamily list for this popup.");
        }
      }
    };

    savingRef.current = false;
    setPendingRequest(null);
    setForm(buildEmptyForm());
    setSaving(false);
    setError("");
    loadSubfamilies();

    return () => {
      cancelled = true;
    };
  }, [isOpen, canCreate, languageId, access, permission]);

  if (!isOpen || !canCreate) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;

  const handleClose = () => {
    if (savingRef.current) return;
    setForm(buildEmptyForm());
    setError("");
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current || !access.can(permission)) return;
    if (hasInternalCodeConflict(form.code, existingRecords)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }
    const stamp = access.capture();
    savingRef.current = true;
    setSaving(true);
    setError("");

    try {
      const response = await axios.post("/api/purchasing/categories", { ...form, code: form.code.trim() }, access.requestConfig(permission));
      if (!access.isCurrent(stamp, permission)) return;
      if (isPendingCatalogCreation(response.data)) { setPendingRequest(response.data.request); setSaving(false); return; }
      if (typeof onAdded === "function") {
        await onAdded(response.data);
      }
      setForm(buildEmptyForm());
      onClose();
    } catch (requestError) {
      if (!access.isCurrent(stamp, permission)) return;
      console.error("[CATEGORIES][ADD][ERROR]", requestError);
      savingRef.current = false;
      setError(catalogRequestError(requestError, "Error saving category.", form.code));
      setSaving(false);
    }
  };

  return (
    <div
      className="spx-modalBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >


      <div ref={dialogRef} className="spx-modal" role="dialog" aria-modal="true" aria-label={t("Add category")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Structure")}</div>
            <h3 className="spx-modalTitle">{t("Add Category")}</h3>
            <p className="spx-modalSubtitle">{t("Select its subfamily, then give this category a code and name.")}</p>
          </div>

          <button type="button" className="spx-closeBtn" onClick={handleClose} disabled={saving} aria-label={t("Close add category popup")}>
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="spx-modalBody">
          <div className="spx-formGrid">
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="category_subfamily">{t("Subfamily")} </label>
              <select
                id="category_subfamily"
                name="subfamily_id"
                value={form.subfamily_id}
                onChange={handleChange}
                disabled={saving}
                className="spx-select"
                required
              >
                <option value="">{t("Select a subfamily")}</option>
                {subfamilies.map((subfamily) => (
                  <option key={subfamily.id} value={subfamily.id}>
                    {subfamily.family?.name ? `${subfamily.family.name} / ` : ""}
                    {subfamily.code ? `${subfamily.code} - ` : ""}
                    {subfamily.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="category_name">{t("Category Name")} </label>
              <input
                id="category_name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: Fresh Produce")}
                required
              />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="category_code">{t("Category Code")} </label>
              <input
                id="category_code"
                type="text"
                name="code"
                value={form.code}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: CAT001")}
                required
              />
              <InternalCodeSuggestions name={form.name} value={form.code} resource="categories" disabled={saving}
                onSuggest={(code) => { setForm((previous) => ({ ...previous, code })); setError(""); }} />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="category_description">{t("Description")} </label>
              <textarea
                id="category_description"
                name="description"
                value={form.description}
                onChange={handleChange}
                disabled={saving}
                className="spx-textarea"
                placeholder={t("Optional operational explanation for how this category should be used.")}
              />
            </div>
          </div>

          {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

          <div className="spx-modalFooter">
            <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={saving}>{t("Cancel")} </button>
            <button type="submit" className="spx-btn spx-btn--primary" disabled={saving}>
              {t(saving ? "Saving..." : "Save Category")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
